import { isEmpty } from 'lodash';
import { memo, useCallback, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  FormArrayFields,
  FormSection,
  FormSwitch,
  FormTextFieldInlineEdit,
  List,
  ListItem,
} from '@common-components';
import { useFormProvider, useIsMobile, useProcessNewLabels } from 'hooks';
import useUpdateNodeFormData from 'pages/app-pages/chatbot-builder-page/hooks/useUpdateNodeFormData';
import FormLabelsSelector from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/FormLabelsSelector';
import NodesFormTextEditor from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/NodesFormTextEditor';
import StepFormFieldsWrapper from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormFieldsWrapper';
import StepFormSubmitButtons from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormSubmitButtons';
import StepFormWrapper from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormWrapper';

import NavigateFormArrayField from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/navigate/NavigateFormArrayField';
import { NavigateStepFormFields } from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/navigate/types';
import {
  defaultNavigateStepFormValues,
  navigateFormFieldsConfig,
  navigateStepFormSchema,
} from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/navigate/utils';
import { Mention } from 'types';
import { characterLimitValue } from 'utils';

interface MessageProps {
  id: string;
  defaultValues?: Partial<NavigateStepFormFields>;
  mentions?: Mention[];
  commands?: Mention[];
}

const NavigateStep = ({ defaultValues, mentions, commands, id }: MessageProps) => {
  const [localDefaultValues, setLocalDefaultValues] = useState({
    ...defaultNavigateStepFormValues,
    ...defaultValues,
    nodeId: defaultValues?.nodeId || uuid(),
    links: isEmpty(defaultValues?.links)
      ? [{ name: 'Link 1', text: '', url: '', nodeId: uuid() }]
      : defaultValues?.links,
    labels: defaultValues?.labels || [],
  });

  const onLocalDefaultValuesChange = useCallback(
    (newDefaultValues: Partial<NavigateStepFormFields>) => {
      setLocalDefaultValues((prev) => ({
        ...prev,
        ...newDefaultValues,
      }));
    },
    [],
  );

  const formMethods = useFormProvider<NavigateStepFormFields>({
    schema: navigateStepFormSchema,
    defaultValues: localDefaultValues,
  });

  const updateNodeFormData = useUpdateNodeFormData();

  const { isMobile } = useIsMobile();

  const localCommands = useRef(commands);
  const localMentions = useRef(mentions);

  const processNewLabels = useProcessNewLabels();

  const { reset, formState } = formMethods;

  const handleOnSubmit = async (formData: NavigateStepFormFields) => {
    const labelIds = await processNewLabels(formData.labels);

    await updateNodeFormData({
      id,
      formData: {
        ...formData,
        labels: labelIds,
      },
    });
  };

  const { name, labels, links, input, optionFullWidth } = navigateFormFieldsConfig;

  return (
    <StepFormWrapper formMethods={formMethods} handleOnSubmit={handleOnSubmit}>
      <StepFormFieldsWrapper>
        <FormTextFieldInlineEdit {...name} variant='standard' size='small' />
        <FormLabelsSelector {...labels} onLocalDefaultValuesChange={onLocalDefaultValuesChange} />

        <NodesFormTextEditor
          characterLimit={characterLimitValue}
          autoFocus={!isMobile}
          {...input}
          mentions={localMentions.current}
          commands={localCommands.current}
        />
        <FormSection title='Link Buttons'>
          <FormArrayFields
            name={links.name}
            FormArrayFieldComponent={NavigateFormArrayField}
            addButtonText='Add Link'
          />
        </FormSection>
        <List divider label='Additional Options'>
          <ListItem>
            <FormSwitch {...optionFullWidth} />
          </ListItem>
        </List>
      </StepFormFieldsWrapper>
      <StepFormSubmitButtons formState={formState} reset={reset} />
    </StepFormWrapper>
  );
};

export default memo(NavigateStep);
